import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AnalyticsService, ConversationService } from "@modules/core";
import { NewExternalChatDialogComponent } from "@modules/external/new-external-chat-dialog/new-external-chat-dialog.component";
import { UpgradePromptDialogComponent } from "@modules/shared/upgrade-prompt-dialog/upgrade-prompt-dialog.component";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  forkJoin,
  of,
} from "rxjs";
import {
  catchError,
  concatMap,
  first,
  map,
  mapTo,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  ConversationModelV2,
  ConversationType,
  CreateMessageMediaModel,
  CreateMessageModel,
  CreateMessagePatientFileModel,
  FeatureState,
  IdentityVerificationStatus,
  PatientDataModel,
  ProfessionVerificationStatus,
  Team,
  VerificationStatus,
} from "types";
import { SubscriptionContainer, UserLimitsUtils, concatNotNull } from "utils";
import {
  AccountService,
  UserLimits,
  UserUsage,
} from "../core/services/account.service";
import { ConversationsService } from "../core/services/conversations.service";
import { TeamsService } from "./../core/services/teams.service";
import {
  UserSelectionList,
  UserSelectionListItem,
} from "./../shared/basic-user-selection-list/basic-user-selection-list.component";
import {
  BasicUserPickerData,
  BasicUserPickerService,
} from "./basic-user-picker.service";
import { UserPickerSubmitCallback } from "./user-picker.service";

export enum NetworkUserPickerMode {
  CREATE,
  MULTIPLE_SELECT,
}

export type NetworkUserPickerPurpose =
  | "ConversationCreate"
  | "Forward"
  | "SecureLibraryShare";

export interface NetworkUserPickerData extends BasicUserPickerData {
  variant: "network-user-picker";
  disabledUserIds?: string[];
  initiallySelectedUserIds?: string[];
  mode?: NetworkUserPickerMode;
  excludeTeams?: boolean;
  excludeConnections?: boolean;
  excludeSuggestions?: boolean;
  workspaceId?: string | null;
  excludeSelf?: boolean;
  initiallyExpandedWorkspaceIds?: string[] | null;
  submitCallback?: UserPickerSubmitCallback;
  asTeamId?: string | null;

  /**
   * Only applies if mode is `NetworkUserPickerMode.CREATE`. This message will be sent to the chat that's created
   * by this picker.
   */
  message?: CreateMessageModel | null;

  /**
   * Only applies if mode is `NetworkUserPickerMode.CREATE`. These files will be sent to the chat that's created
   * by this picker.
   */
  media?: CreateMessageMediaModel[] | null;

  /**
   * Only applies if mode is `NetworkUserPickerMode.CREATE`. These files will be sent to the chat that's created
   * by this picker.
   */
  patientFiles?: CreateMessagePatientFileModel[] | null;

  /** The purpose of this picker invocation. Used to determine analytics referrer on limit prompts. */
  purpose?: NetworkUserPickerPurpose;
}

export enum NetworkUserPickerState {
  CREATE_CHAT,
  CREATE_GROUP,
  CREATE_CASE,
  MULTIPLE_SELECT,
}

type LimitedConversationType = "chat" | "group" | "case";
type ConversationLimitDisplayType = LimitedConversationType | "external";

@Injectable()
export class NetworkUserPickerService implements OnDestroy {
  private stateSubject = new BehaviorSubject<NetworkUserPickerState>(
    NetworkUserPickerState.CREATE_CHAT
  );

  public state$ = this.stateSubject.asObservable();

  private asTeamSubject = new BehaviorSubject<Team | null>(null);
  public asTeam$ = this.asTeamSubject.asObservable();

  private hideSuggestionsSubject = new BehaviorSubject<boolean>(false);
  public hideSuggestions$ = this.hideSuggestionsSubject.asObservable();

  private initiallyExpandedWorkspaceIdsSubject = new BehaviorSubject<string[]>(
    []
  );

  public initiallyExpandedWorkspaceIds$ =
    this.initiallyExpandedWorkspaceIdsSubject.asObservable();

  public isDiscoverable$: Observable<boolean> | null =
    this.accountService.isDiscoverable$;

  public isVerified$ = this.accountService.isVerified$;
  public userLimits$ = this.accountService.userLimits$;
  public userUsage$ = this.accountService.userUsage$;

  public isConversationLimitNoticeVisible$ = this.userLimits$.pipe(
    map((userLimits) => {
      const creationLimits = userLimits?.conversation_creation_limit;
      if (!creationLimits) return false;

      const { chatsLimit, groupsLimit, patientCasesLimit } = creationLimits;
      return [chatsLimit, groupsLimit, patientCasesLimit].some(
        (limit) => typeof limit === "number" && limit !== -1
      );
    })
  );

  public isGroupConversationLimitReached$ = combineLatest([
    this.userLimits$,
    this.userUsage$,
  ]).pipe(
    map(([userLimits, userUsage]) => {
      return this.hasReachedConversationLimitWithNullableData(
        "group",
        userLimits,
        userUsage
      );
    })
  );

  public isCaseConversationLimitReached$ = combineLatest([
    this.userLimits$,
    this.userUsage$,
  ]).pipe(
    map(([userLimits, userUsage]) => {
      return this.hasReachedConversationLimitWithNullableData(
        "case",
        userLimits,
        userUsage
      );
    })
  );

  public groupConversationRemainingText$ = combineLatest([
    this.userLimits$,
    this.userUsage$,
  ]).pipe(
    map(([userLimits, userUsage]) => {
      return this.getConversationRemainingTextWithNullableData(
        "group",
        userLimits,
        userUsage
      );
    })
  );

  public caseConversationRemainingText$ = combineLatest([
    this.userLimits$,
    this.userUsage$,
  ]).pipe(
    map(([userLimits, userUsage]) => {
      return this.getConversationRemainingTextWithNullableData(
        "case",
        userLimits,
        userUsage
      );
    })
  );

  public externalConversationRemainingText$ = combineLatest([
    this.userLimits$,
    this.userUsage$,
  ]).pipe(
    map(([userLimits, userUsage]) => {
      return this.getConversationRemainingTextWithNullableData(
        "external",
        userLimits,
        userUsage
      );
    })
  );

  public isNewExternalChatEnabled$ = this.accountService.userLimits$.pipe(
    map(
      (userLimits) =>
        userLimits && UserLimitsUtils.isNewExternalChatVisible(userLimits)
    )
  );

  private userPickerService: BasicUserPickerService | null = null;
  private data: NetworkUserPickerData | null = null;

  private subscriptions = new SubscriptionContainer();

  public constructor(
    private accountService: AccountService,
    private conversationsService: ConversationsService,
    private teamsService: TeamsService,
    private conversationService: ConversationService,
    private matDialog: MatDialog,
    private analyticsService: AnalyticsService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public initialise(
    userPickerService: BasicUserPickerService,
    data: NetworkUserPickerData
  ) {
    this.userPickerService = userPickerService;
    this.data = data;

    this.accountService.getUserUsage().subscribe();
    this.userPickerService?.user$.subscribe({
      next: (user) => {
        if (!user) return;
        this.handleUser(user);
      },
    });

    if (data.asTeamId) {
      const teamSubscription = this.teamsService
        .getTeam({ teamId: data.asTeamId })
        .subscribe((team) => {
          this.asTeamSubject.next(team);
        });
      this.subscriptions.add(teamSubscription);
    }

    this.hideSuggestionsSubject.next(data.excludeSuggestions ?? false);
    this.initiallyExpandedWorkspaceIdsSubject.next(
      data.initiallyExpandedWorkspaceIds ?? []
    );

    this.state$.subscribe(this.handleState.bind(this));

    if (data.mode === NetworkUserPickerMode.MULTIPLE_SELECT) {
      this.stateSubject.next(NetworkUserPickerState.MULTIPLE_SELECT);
    }
  }

  private selectCurrentuser() {
    const account = this.accountService.getCurrentAccount();
    if (
      !account ||
      !account.userId ||
      !account.workplaces ||
      !account.professions
    ) {
      throw new Error("User account is invalid or undefined");
    }
    this.userPickerService?.updateUser({
      id: account.userId,
      name: concatNotNull([account.firstName, account.lastName]),
      isWorkspaceVerified: account.workplaces.some(
        (w) =>
          w.isActive && w.verificationStatus === VerificationStatus.Verified
      ),
      isIdentityVerified:
        account.identityVerificationStatus ===
        IdentityVerificationStatus.Verified,
      isProfessionVerified: account.professions.some(
        (p) => p.verificationStatus === ProfessionVerificationStatus.Verified
      ),
      isSelected: true,
      isDisabled: true,
      fetchImage: account.picture != null,
      suffix: " (You)",
    });
  }

  public onCreateChat() {
    this.stateSubject.next(NetworkUserPickerState.CREATE_CHAT);
    this.userPickerService?.setTeamsVisibility(true);
  }

  public onCreateGroup() {
    this.withConversationLimitGuard("group", () => {
      this.selectCurrentuser();
      this.stateSubject.next(NetworkUserPickerState.CREATE_GROUP);
      this.userPickerService?.setTeamsVisibility(false);
    });
  }

  public onCreateCase() {
    this.withConversationLimitGuard("case", () => {
      this.selectCurrentuser();
      this.stateSubject.next(NetworkUserPickerState.CREATE_CASE);
      this.userPickerService?.setTeamsVisibility(false);
    });
  }

  public onCreateExternal() {
    this.matDialog.afterAllClosed
      .pipe(
        switchMap(() =>
          forkJoin({
            userLimits: this.accountService.userLimits$.pipe(
              first((v) => v !== null)
            ),
            userUsage: this.accountService.userUsage$.pipe(first()),
          })
        ),
        first()
      )
      .subscribe({
        next: ({ userLimits, userUsage }) => {
          if (userLimits.external_conversations === FeatureState.Upgradeable) {
            UpgradePromptDialogComponent.openDialog(this.matDialog, {
                limitType: "External",
                referrer: this.getCreationReferrer(),
              })
              .afterOpened()
              .subscribe(() => {
                this.analyticsService
                  .logEvent({
                    eventType: "FreePlanLimitPromptView",
                    timestamp: new Date().toISOString(),
                    limitType: "External",
                    referrer: this.getCreationReferrer(),
                    currentCount:
                      userUsage?.conversationUsage
                        ?.externalConversationsCreated,
                    maxAllowed:
                      userLimits.conversation_creation_limit
                        ?.externalConversationsLimit,
                  })
                  .subscribe();
              });
          } else if (
            userLimits.external_conversations === FeatureState.Enabled
          ) {
            NewExternalChatDialogComponent.openDialog(this.matDialog)
              .afterClosed()
              .subscribe({
                next: (result) => {
                  if (!result?.conversationId) return;
                  this.conversationsService.navigateToExternalConversation(
                    result?.conversationId
                  );
                },
              });
          }
        },
      });
    this.matDialog.closeAll();
  }

  public onBack() {
    this.userPickerService?.reset();
    this.stateSubject.next(
      this.data?.mode !== NetworkUserPickerMode.MULTIPLE_SELECT
        ? NetworkUserPickerState.CREATE_CHAT
        : NetworkUserPickerState.MULTIPLE_SELECT
    );
    this.userPickerService?.setTeamsVisibility(true);
  }

  private handleState(state: NetworkUserPickerState) {
    if (!this.userPickerService) return;

    if (this.data?.header) {
      this.userPickerService.setHeader(this.data?.header);
      return;
    }

    switch (state) {
      case NetworkUserPickerState.CREATE_CHAT:
        this.userPickerService.setHeader("New Chat");
        break;
      case NetworkUserPickerState.CREATE_GROUP:
        this.userPickerService.setHeader("New Group");
        break;
      case NetworkUserPickerState.CREATE_CASE:
        this.userPickerService.setHeader("New Case");
        break;
    }
  }

  private submit(
    submitCallback: (
      selectedUserIds: string[]
    ) => Observable<ConversationModelV2>,
    minimumSelectionSize: number = 1,
    refreshUsageOnSuccess: boolean = false
  ) {
    const callback: UserPickerSubmitCallback = (users: UserSelectionList) => {
      const selectedUserIds = users.map((u) => u.id);

      // Get or create conversation
      let conversation$ = submitCallback(selectedUserIds);

      // Send/share media to conversation
      if (this.data?.mode === NetworkUserPickerMode.CREATE) {
        conversation$ = conversation$.pipe(
          concatMap((conversation) => {
            if (!conversation.id) throw new Error("Invalid conversation id");
            if (!this.data) return of(conversation);

            const { message, media, patientFiles } = this.data;
            if (!message && !media?.length && !patientFiles?.length) {
              return of(conversation);
            }

            const observables: Observable<unknown>[] = [];

            if (this.data?.message) {
              observables.push(
                this.conversationsService.sendMessage(
                  conversation.id,
                  this.data.message
                )
              );
            }

            if (this.data?.media?.length) {
              observables.push(
                this.conversationsService.sendMedia(
                  conversation.id,
                  this.data.media
                )
              );
            }

            if (this.data?.patientFiles?.length) {
              observables.push(
                this.conversationsService.sendPatientFiles(
                  conversation.id,
                  this.data.patientFiles
                )
              );
            }

            return forkJoin(observables).pipe(mapTo(conversation));
          })
        );
      }

      return conversation$.pipe(
        tap(() => {
          if (!refreshUsageOnSuccess) return;
          this.accountService.getUserUsage().subscribe();
        }),
        switchMap((conversation) => {
          // Add conversation to legacy conversation service
          this.conversationService.addOrReplaceConversation(conversation);

          // Navigate to conversation after it's been created
          if (!conversation.id) throw new Error("Invalid conversation id");
          if (conversation.type !== ConversationType.TeamChat) {
            this.conversationsService.navigateToConversation(conversation.id);
            return EMPTY;
          }

          const isMember = this.data?.asTeamId
            ? conversation.teamIds?.includes(this.data?.asTeamId)
            : false;

          if (isMember) {
            this.conversationsService.navigateToConversation(
              conversation.id,
              this.data?.asTeamId
            );
          } else {
            this.conversationsService.navigateToConversation(conversation.id);
          }
          return EMPTY;
        })
      );
    };

    this.userPickerService?.setData(callback);
    this.userPickerService?.submitSelectedUsers(minimumSelectionSize);
  }

  private handleUser(user: UserSelectionListItem) {
    if (!user) return;
    if (this.stateSubject.value !== NetworkUserPickerState.CREATE_CHAT) return;

    const isSharingOrForwarding =
      this.data?.message ||
      this.data?.media?.length ||
      this.data?.patientFiles?.length;

    const submitChat = () => {
      this.submit(
        () => {
          const asTeamId = this.asTeamSubject.value?.id;
          if (asTeamId) {
            if (user.isTeam)
              throw new Error("You cannot create a chat with multiple teams");
            return this.conversationsService.createTeamChat(asTeamId, user.id);
          } else {
            if (user.isTeam) {
              return this.conversationsService.createTeamChat(user.id);
            }
            return this.conversationsService.createChat(user.id);
          }
        },
        0,
        true
      );
    };

    const asTeamId = this.asTeamSubject.value?.id;
    const chatId =
      !asTeamId && !user.isTeam
        ? this.conversationsService.getChatId(user.id)
        : null;

    // Fast path: conversation already in memory — navigate instantly with no API call
    if (chatId && this.conversationsService.isChatInMemory(user.id)) {
      if (isSharingOrForwarding) {
        submitChat();
        return;
      }

      this.matDialog.closeAll();
      this.conversationsService.navigateToConversation(chatId);
      return;
    }

    // Conversation not in memory — check via API before applying the upgrade limit guard
    this.userPickerService?.setLoading(true);
    this.conversationsService
      .chatExists(user.id)
      .pipe(
        first(),
        catchError(() => of(false))
      )
      .subscribe((exists) => {
        this.userPickerService?.setLoading(false);
        if (!isSharingOrForwarding && exists && chatId) {
          this.matDialog.closeAll();
          this.conversationsService.navigateToConversation(chatId);
        } else if (exists) {
          submitChat();
        } else {
          this.withConversationLimitGuard("chat", () => submitChat());
        }
      });
  }

  public createGroup(conversationName: string) {
    this.withConversationLimitGuard("group", () => {
      this.submit(
        (selectedUserIds) => {
          return this.conversationsService.createGroup(
            conversationName,
            selectedUserIds
          );
        },
        1,
        true
      );
    });
  }

  public createCase(conversationName: string, patientData: PatientDataModel) {
    this.withConversationLimitGuard("case", () => {
      this.submit(
        (selectedUserIds) => {
          return this.conversationsService.createCase(
            conversationName,
            selectedUserIds,
            patientData
          );
        },
        1,
        true
      );
    });
  }

  public openConversationLimitsLearnMore() {
    UpgradePromptDialogComponent.openDialog(this.matDialog, {
      referrer: this.getCreationReferrer(),
    })
      .afterOpened()
      .subscribe(() => {
        this.analyticsService
          .logEvent({
            eventType: "FreePlanLimitPromptView",
            timestamp: new Date().toISOString(),
            referrer: this.getCreationReferrer(),
          })
          .subscribe();
      });
  }

  private getCreationReferrer(): string {
    return this.data?.purpose ?? "ConversationCreate";
  }

  private withConversationLimitGuard(
    type: LimitedConversationType,
    onAllowed: () => void
  ) {
    this.getConversationLimitsAndUsage().subscribe({
      next: ({ userLimits, userUsage }) => {
        if (this.hasReachedConversationLimit(type, userLimits, userUsage)) {
          const limits = userLimits.conversation_creation_limit;
          const usage = userUsage.conversationUsage;
          const { limit, used } =
            type === "chat"
              ? { limit: limits?.chatsLimit, used: usage?.chatsCreated }
              : type === "group"
                ? { limit: limits?.groupsLimit, used: usage?.groupsCreated }
                : {
                    limit: limits?.patientCasesLimit,
                    used: usage?.patientCasesCreated,
                  };

          UpgradePromptDialogComponent.openDialog(this.matDialog, {
            limitType:
              type === "chat" ? "Chat" : type === "group" ? "Group" : "Case",
            referrer: this.getCreationReferrer(),
          })
            .afterOpened()
            .subscribe(() => {
              this.analyticsService
                .logEvent({
                  eventType: "FreePlanLimitPromptView",
                  timestamp: new Date().toISOString(),
                  limitType:
                    type === "chat"
                      ? "Chat"
                      : type === "group"
                        ? "Group"
                        : "Case",
                  referrer: this.getCreationReferrer(),
                  currentCount: used,
                  maxAllowed: limit,
                })
                .subscribe();
            });
          return;
        }

        onAllowed();
      },
    });
  }

  private getConversationLimitsAndUsage(): Observable<{
    userLimits: UserLimits;
    userUsage: UserUsage;
  }> {
    return forkJoin({
      userLimits: this.userLimits$.pipe(
        first(),
        switchMap((userLimits) => {
          if (userLimits) return of(userLimits);
          return this.accountService.getUserLimits();
        })
      ),
      userUsage: this.userUsage$.pipe(
        first(),
        switchMap((userUsage) => {
          if (userUsage) return of(userUsage);
          return this.accountService.getUserUsage();
        })
      ),
    });
  }

  private hasReachedConversationLimit(
    type: LimitedConversationType,
    userLimits: UserLimits,
    userUsage: UserUsage
  ): boolean {
    const conversationLimits = userLimits.conversation_creation_limit;
    const conversationUsage = userUsage.conversationUsage;

    if (!conversationLimits || !conversationUsage) return false;

    let limit: number | undefined;
    let used: number | undefined;

    switch (type) {
      case "chat":
        limit = conversationLimits.chatsLimit;
        used = conversationUsage.chatsCreated;
        break;
      case "group":
        limit = conversationLimits.groupsLimit;
        used = conversationUsage.groupsCreated;
        break;
      case "case":
        limit = conversationLimits.patientCasesLimit;
        used = conversationUsage.patientCasesCreated;
        break;
    }

    if (typeof limit !== "number" || limit === -1) return false;
    return (used ?? 0) >= limit;
  }

  private hasReachedConversationLimitWithNullableData(
    type: LimitedConversationType,
    userLimits: UserLimits | null,
    userUsage: UserUsage | null
  ): boolean {
    if (!userLimits || !userUsage) return false;
    return this.hasReachedConversationLimit(type, userLimits, userUsage);
  }

  private getConversationRemainingText(
    type: ConversationLimitDisplayType,
    userLimits: UserLimits,
    userUsage: UserUsage
  ): string | null {
    const conversationLimits = userLimits.conversation_creation_limit;
    const conversationUsage = userUsage.conversationUsage;

    if (!conversationLimits || !conversationUsage) return null;

    let limit: number | undefined;
    let used: number | undefined;

    switch (type) {
      case "chat":
        limit = conversationLimits.chatsLimit;
        used = conversationUsage.chatsCreated;
        break;
      case "group":
        limit = conversationLimits.groupsLimit;
        used = conversationUsage.groupsCreated;
        break;
      case "case":
        limit = conversationLimits.patientCasesLimit;
        used = conversationUsage.patientCasesCreated;
        break;
      case "external":
        limit = conversationLimits.externalConversationsLimit;
        used = conversationUsage.externalConversationsCreated;
        break;
    }

    if (typeof limit !== "number" || limit === -1) return null;

    const remaining = Math.max(limit - (used ?? 0), 0);
    return `${remaining} of ${limit} remaining`;
  }

  private getConversationRemainingTextWithNullableData(
    type: ConversationLimitDisplayType,
    userLimits: UserLimits | null,
    userUsage: UserUsage | null
  ): string | null {
    if (!userLimits || !userUsage) return null;
    return this.getConversationRemainingText(type, userLimits, userUsage);
  }

  public save() {
    this.userPickerService?.submitSelectedUsers();
  }
}
